/**
 * CatalogueFilterBody Component
 *
 * Renders catalogue chips grouped by year (descending) inside the FiltersModal body.
 * Each chip toggles its catalogue in/out of the selected set.
 */

// React
import { useMemo } from 'react';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { CHIP_UNSELECTED, CHIP_SELECTED } from './filterChipStyles';

// Types
import type { ApiCatalogue } from '@api/catalogues';

/* ---- Layout constants ---- */
const GRID_CLASSES = 'grid grid-cols-4 gap-3';
const SECTION_GAP = 'gap-6';

/* ---- Chip classes ---- */
const CHIP_BASE = 'border rounded-input py-3 px-5 text-center font-inter text-sm leading-normal cursor-pointer select-none outline-none transition-[color,background-color,border-color,transform] ease-in-out active:scale-95 focus-visible:border-brand-yellow';

/* ---- Types ---- */
interface CatalogueFilterBodyProps {
  catalogues: ApiCatalogue[];
  selectedIds: number[];
  onToggle: (id: number) => void;
}

interface YearGroup {
  year: number;
  catalogues: ApiCatalogue[];
}

/* ---- Helpers ---- */
function groupByYear(catalogues: ApiCatalogue[]): YearGroup[] {
  const map = new Map<number, ApiCatalogue[]>();
  for (const catalogue of catalogues) {
    const group = map.get(catalogue.year) ?? [];
    group.push(catalogue);
    map.set(catalogue.year, group);
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => b - a)
    .map(([year, items]) => ({
      year,
      catalogues: items.sort((a, b) => a.name.localeCompare(b.name)),
    }));
}

/* ---- Component ---- */
export function CatalogueFilterBody({ catalogues, selectedIds, onToggle }: CatalogueFilterBodyProps) {
  const groups = useMemo(() => groupByYear(catalogues), [catalogues]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  return (
    <div className={cn('flex flex-col', SECTION_GAP)}>
      {groups.map(({ year, catalogues: items }) => (
        <div key={year} className="flex flex-col gap-3">
          <span className="font-inter text-sm font-semibold leading-normal text-grey">
            {year}
          </span>
          <div className={GRID_CLASSES}>
            {items.map((catalogue) => {
              const isSelected = selectedSet.has(catalogue.id);
              return (
                <button
                  key={catalogue.id}
                  type="button"
                  role="checkbox"
                  aria-checked={isSelected}
                  className={cn(CHIP_BASE, isSelected ? CHIP_SELECTED : CHIP_UNSELECTED)}
                  onClick={() => onToggle(catalogue.id)}
                >
                  {catalogue.name}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
